import React, {useContext, useEffect, useState, useCallback} from 'react';
import useAxios from 'axios-hooks';
import {getLocationData, getAllZipCodes} from "../../../../Api";
import {
  FormControl,
  InputLabel,
  Backdrop,
  CircularProgress,
  TextField,
  Select,
  MenuItem,
  Typography
} from "@material-ui/core";
import {Autocomplete} from "@material-ui/lab";
import { createFilterOptions } from '@material-ui/lab/Autocomplete';
import {useStyles} from "../styles";
import {WizardContext} from "../context";
import {GENDER_FIELD} from "../constants";

const LocationTypeOptions = [{
  name: 'Nationwide',
  value: 'nationwide',
}, {
  name: 'State / County / City',
  value: 'state_county_city',
}, {
  name: 'Zip code',
  value: 'zip',
}];

const WizardLocation = () => {
  const [locationType, setLocationType] = useState(LocationTypeOptions[0]);

  const [states, setStates] = useState([]);
  const [counties, setCounties] = useState([]);
  const [cities, setCities] = useState([]);
  const [zipCodes, setZipCodes] = useState([]);
  const [{loading: locationLoading, data: baseData}] = useAxios(getLocationData(), { manual: false });
  const [{loading: zipLoading, data: zipCodesData}] = useAxios(getAllZipCodes(), { manual: false });

  const loading = locationLoading || zipLoading;

  const {formData, fieldChange} = useContext(WizardContext);
  const classes = useStyles();

  const changeLocation = (key, value) => {
    if (key === 'distance' && (value > 100 || value < 0)) return;

    fieldChange('location')({target: { value: {
          ...formData.location,
          [key]: value,
        }}});
  };

  const changeLocationType = ({target: {value}}) => {
    setLocationType(LocationTypeOptions.find(lt => lt.value === value));
    fieldChange('location')({target: { value: {
          states: [],
          counties: [],
          cities: [],
          zipCode: null,
          distance: 0,
        }}});
  };

  const filterZipCodes = (options, { inputValue }) => {
    return zipCodes.filter(op => op.indexOf(inputValue) !== -1).slice(0, 30);
  };

  useEffect(() => {
    if (!locationLoading && baseData) {
      setStates(Object.keys(baseData).filter(k => k !== 'raw').map(stateCode => ({
        name: `${stateCode} - ${baseData[stateCode].name}`,
        code: stateCode,
      })));
    }
  }, [baseData, locationLoading]);

  useEffect(() => {
    if (!zipLoading && zipCodesData) {
      setZipCodes(zipCodesData);
    }
  }, [zipCodesData, zipLoading]);

  useEffect(() => {
    if (formData.location.states.length > 0) {
      setLocationType(LocationTypeOptions[1]);
    } else if (formData.location.zipCode !== null) {
      setLocationType(LocationTypeOptions[2]);
    }
  }, []);

  useEffect(() => {
    if (!baseData) return;
    let filteredCounties = [];
    formData.location.states.map(state => {
      const stateData = baseData[state.code];
      if (!stateData) return;

      filteredCounties = [
        ...filteredCounties,
        ...Object.keys(stateData).filter(k => k !== 'name').map(code => ({
            code,
            name: `${stateData[code].name} (${state.code})`,
            stateCode: state.code,
          })),
      ];
    });

    setCounties(filteredCounties);
    changeLocation('counties', formData.location.counties.filter(c => filteredCounties.find(cc => c.code === cc.code)));
  }, [formData.location.states, baseData]);

  useEffect(() => {
    if (!baseData) return;
    let filteredCities = [];
    formData.location.counties.map(county => {
      const stateData = baseData[county.stateCode];
      if (!stateData) return;

      const countyData = stateData[county.code];
      if (!countyData) return;

      filteredCities = [
        ...filteredCities,
        ...Object.keys(countyData).filter(k => k !== 'name').map(cityName => ({
          name: `${cityName} (${county.stateCode})`,
          stateCode: county.stateCode,
          countyCode: county.code,
        })),
      ];
    });

    setCities(filteredCities);
    changeLocation('cities', formData.location.cities.filter(c => filteredCities.find(cc => c.name === cc.name)));
  }, [formData.location.counties, baseData]);

  return (
    <>
      <Backdrop className={classes.backdrop} open={loading}>
        <CircularProgress color="inherit" />
      </Backdrop>
      <FormControl className={classes.formControl}>
        <InputLabel id="gender-field">Location</InputLabel>
        <Select
          labelId="location-field"
          id="location-field"
          value={locationType.value}
          onChange={changeLocationType}
        >
          {LocationTypeOptions.map(lt => (
            <MenuItem key={lt.value} value={lt.value}>{lt.name}</MenuItem>
          ))}
        </Select>
      </FormControl>
      {locationType.value === 'state_county_city' && (
        <>
          <FormControl className={classes.formControl}>
            <Autocomplete
              multiple
              id="states-outlined"
              options={states}
              getOptionLabel={option => option.name}
              defaultValue={[]}
              value={formData.location.states}
              onChange={(e, value) => changeLocation('states', value)}
              filterSelectedOptions
              renderInput={params => (
                <TextField
                  {...params}
                  style={{width: "100%"}}
                  variant="outlined"
                  label="States"
                  placeholder="States"
                />
              )}
            />
          </FormControl>
          <FormControl className={classes.formControl}>
            <Autocomplete
              multiple
              id="counties-outlined"
              options={counties}
              getOptionLabel={option => option.name}
              defaultValue={[]}
              value={formData.location.counties}
              onChange={(e, value) => changeLocation('counties', value)}
              filterSelectedOptions
              renderInput={params => (
                <TextField
                  {...params}
                  style={{width: "100%"}}
                  variant="outlined"
                  label="Counties"
                  placeholder="Counties"
                />
              )}
            />
          </FormControl>
          <FormControl className={classes.formControl}>
            <Autocomplete
              multiple
              id="cities-outlined"
              options={cities}
              getOptionLabel={option => option.name}
              defaultValue={[]}
              value={formData.location.cities}
              onChange={(e, value) => changeLocation('cities', value)}
              filterSelectedOptions
              renderInput={params => (
                <TextField
                  {...params}
                  style={{width: "100%"}}
                  variant="outlined"
                  label="Cities"
                  placeholder="Cities"
                />
              )}
            />
          </FormControl>
        </>
      )}
      {locationType.value === 'zip' && (
        <>
          <FormControl className={classes.formControl}>
            <Autocomplete
              filterOptions={filterZipCodes}
              id="cities-outlined"
              options={zipCodes.slice(0, 100)}
              getOptionLabel={option => option}
              defaultValue={[]}
              value={formData.location.zipCode}
              onChange={(e, value) => changeLocation('zipCode', value)}
              filterSelectedOptions
              renderInput={params => (
                <TextField
                  {...params}
                  style={{width: "100%"}}
                  variant="outlined"
                  label="Zip Code"
                  placeholder="Zip Code"
                />
              )}
            />
          </FormControl>
          <FormControl className={classes.formControl}>
            <Typography id="campaign-question" gutterBottom>
              Zip code radius in Miles (max. 100 Miles)
            </Typography>
            <TextField
              id='campaign-question'
              label=''
              fullWidth
              type='number'
              value={formData.location.distance}
              onChange={({target: { value }}) => changeLocation('distance', value)}
            />
          </FormControl>
        </>
      )}
    </>
  );
};


export default WizardLocation;
